import type { LucideIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMemo, type ReactElement, type ReactNode } from 'react';
import { PostureManagementErrorBoundary } from './components/PostureManagementErrorBoundary';
import type { PostureManagementTabType } from './types';
import { PostureManagementTabs } from './constants';
import {
  POSTURE_MANAGEMENT_TAB_META,
  type PostureManagementTabGates,
} from './tabs';
import { NoTabs } from './components/NoTabs';
import { DataTableUsersContent } from '@/pages/organization/tabs/DataTableUsersTab';
import { DataTableProjectsContent } from '@/pages/organization/tabs/DataTableProjectsTab';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useInventoryTabManagement } from '@/hooks/useInventoryTabManagement';
import DataTableAiInstructionsTab from '@/pages/dashboard/tabs/DataTableAiInstructionsTab';
import DataTableHooksTab from '@/pages/dashboard/tabs/DataTableHooksTab';
import { DataTableSkillsContent } from '@/pages/organization/tabs/DataTableSkillsTab';

interface PostureManagementProps {
  organizationId?: string;
  /** KIR-2917 — gated by HOOK_INVENTORY_ENABLED on the caller. */
  hookInventoryAllowed?: boolean;
  projectsInventoryAllowed?: boolean;
  /** KIR-4699 — gated by SKILLS_INVENTORY on the caller. */
  skillsInventoryAllowed?: boolean;
  /**
   * Whether the user may view org-wide (admin) data. True for owner, manager,
   * and support. Optional: if provided by the parent, avoids a duplicate role
   * check; otherwise computed internally with a loading gate.
   */
  canViewAdminContent?: boolean;
  MCPTabSlot: ReactNode;
  ExtensionsTabSlot?: ReactNode;
  PluginsTabSlot?: ReactNode;
}

interface PostureManagementTabDetails {
  label: string;
  icon: LucideIcon;
  Component: ReactNode;
  guard?: () => boolean;
  /** Optional pill rendered next to the label (e.g. a "NEW" marker). */
  badge?: ReactNode;
}

interface CreateTabsConfigOptions extends PostureManagementTabGates {
  slots: {
    MCPTab: PostureManagementProps['MCPTabSlot'];
    ExtensionsTab?: PostureManagementProps['ExtensionsTabSlot'];
    PluginsTab?: PostureManagementProps['PluginsTabSlot'];
  };
}

type PostureManagementTabConfig = Record<
  PostureManagementTabType,
  PostureManagementTabDetails
>;

const createTabsTrigger = (
  tabsConfig: Record<PostureManagementTabType, PostureManagementTabDetails>
) => {
  return Object.entries(tabsConfig).map(([tabKey, config]) => {
    const showTab = config.guard?.() ?? true;
    if (!showTab) {
      return null;
    }
    return (
      <TabsTrigger
        className="flex items-center justify-center gap-1 !whitespace-normal sm:!whitespace-nowrap text-xs sm:text-sm h-auto sm:h-[calc(100%-1px)] py-2 sm:py-1"
        value={tabKey}
        key={tabKey}
      >
        <config.icon className="size-5 shrink-0" />
        <span className="break-words text-center">{config.label}</span>
        {config.badge}
      </TabsTrigger>
    );
  });
};

const createTabsContent = (
  tabsConfig: Record<PostureManagementTabType, PostureManagementTabDetails>
) => {
  return Object.entries(tabsConfig).map(([tabKey, config]) => {
    const showTab = config.guard?.() ?? true;
    if (!showTab) {
      return null;
    }
    return (
      <TabsContent
        value={tabKey}
        key={tabKey}
        className="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <PostureManagementErrorBoundary
          tab={tabKey as PostureManagementTabType}
        >
          {config.Component}
        </PostureManagementErrorBoundary>
      </TabsContent>
    );
  });
};

const NEW_BADGE = (
  <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-blue-700">
    New
  </span>
);

/**
 * Labels, icons, order and gates come from POSTURE_MANAGEMENT_TAB_META (shared
 * with the UI v2 sidebar); this only attaches what each tab renders. A tab
 * whose content is null — a slot the caller left out, or an org-scoped tab
 * without an organizationId — is hidden.
 */
const createTabsConfig = ({
  slots: { MCPTab, ExtensionsTab, PluginsTab },
  ...gates
}: CreateTabsConfigOptions): PostureManagementTabConfig => {
  const { organizationId } = gates;
  const components: Record<PostureManagementTabType, ReactNode> = {
    [PostureManagementTabs.USER_SECURITY_POSTURE]: organizationId ? (
      <DataTableUsersContent organizationId={organizationId} />
    ) : null,
    [PostureManagementTabs.PROJECTS]: organizationId ? (
      <DataTableProjectsContent organizationId={organizationId} />
    ) : null,
    [PostureManagementTabs.MCP]: MCPTab,
    [PostureManagementTabs.EXTENSIONS]: ExtensionsTab,
    [PostureManagementTabs.PLUGINS]: PluginsTab,
    [PostureManagementTabs.AI_INSTRUCTIONS]: organizationId ? (
      <DataTableAiInstructionsTab
        organizationId={organizationId}
        skillsInventoryAllowed={gates.skillsInventoryAllowed}
      />
    ) : null,
    // Keyed on the org: the panel is keyed by tab id, so switching
    // organizations would otherwise reuse this instance and keep its local
    // state — an open "Mark for delete" dialog would survive the switch and
    // write the previous org's skill into the new one.
    [PostureManagementTabs.SKILLS]: organizationId ? (
      <DataTableSkillsContent
        key={organizationId}
        organizationId={organizationId}
      />
    ) : null,
    // Org view lists the org's hooks; the personal dashboard the caller's own.
    [PostureManagementTabs.HOOKS]: organizationId ? (
      <DataTableHooksTab scope={{ kind: 'organization', organizationId }} />
    ) : (
      <DataTableHooksTab scope={{ kind: 'account' }} />
    ),
  };
  const entries = (
    Object.keys(POSTURE_MANAGEMENT_TAB_META) as PostureManagementTabType[]
  ).map(key => {
    const meta = POSTURE_MANAGEMENT_TAB_META[key];
    const Component = components[key];
    const details: PostureManagementTabDetails = {
      label: meta.label,
      icon: meta.icon,
      badge: meta.isNew ? NEW_BADGE : undefined,
      Component,
      guard: () => meta.visible(gates) && Component != null,
    };
    return [key, details] as const;
  });
  return Object.fromEntries(entries) as PostureManagementTabConfig;
};

export const PostureManagement = ({
  organizationId,
  canViewAdminContent: canViewAdminContentProp,
  hookInventoryAllowed,
  projectsInventoryAllowed,
  skillsInventoryAllowed,
  MCPTabSlot,
  ExtensionsTabSlot,
  PluginsTabSlot,
}: PostureManagementProps): ReactElement | null => {
  const isHookInventoryEnabled = hookInventoryAllowed ?? false;

  // One read-scope role check (owner/manager/support), falling back to an
  // internal hook when the parent doesn't pass the prop (e.g. Dashboard,
  // tests). It gates Users, Projects, AI Instructions, and Hooks — support
  // reads every org surface (KIR-4128).
  // When the parent already supplies the role, pass `undefined` to the
  // internal hook so its underlying useRoleCheck query stays disabled — the
  // hook still runs (rules-of-hooks) but does no network work.
  const adminContentCheck = useCanViewAdminContent(
    canViewAdminContentProp === undefined ? organizationId : undefined
  );
  const canViewAdminContent =
    canViewAdminContentProp ?? adminContentCheck.canViewAdminContent;

  // Gate render until an internally-resolved role check finishes, so the
  // default-tab selection doesn't race role data.
  const needsLoadingGate =
    canViewAdminContentProp === undefined && adminContentCheck.isLoading;

  const tabsConfig = createTabsConfig({
    organizationId,
    canViewAdminContent,
    hookInventoryAllowed: isHookInventoryEnabled,
    projectsInventoryAllowed: projectsInventoryAllowed ?? false,
    skillsInventoryAllowed: skillsInventoryAllowed ?? false,
    slots: {
      MCPTab: MCPTabSlot,
      ExtensionsTab: ExtensionsTabSlot,
      PluginsTab: PluginsTabSlot,
    },
  });

  // Manage tab state and URL synchronization
  const { currentTab, handleTabChange } = useInventoryTabManagement({
    tabsConfig,
  });

  const triggers = createTabsTrigger(tabsConfig);
  const contents = createTabsContent(tabsConfig);

  const visibleTabCount = useMemo(
    () =>
      Object.values(tabsConfig).filter(config => config.guard?.() ?? true)
        .length,
    [tabsConfig]
  );
  const hasTabs = visibleTabCount > 0;
  const tabsListGridCols =
    visibleTabCount === 1
      ? 'sm:grid-cols-1'
      : visibleTabCount === 2
        ? 'sm:grid-cols-2'
        : visibleTabCount === 3
          ? 'sm:grid-cols-3'
          : visibleTabCount === 4
            ? 'sm:grid-cols-4'
            : visibleTabCount === 5
              ? 'sm:grid-cols-5'
              : visibleTabCount === 6
                ? 'sm:grid-cols-6'
                : visibleTabCount === 7
                  ? 'sm:grid-cols-7'
                  : 'sm:grid-cols-8';

  // Wait for user data to load before rendering tabs (when using internal role check)
  // This prevents race condition with defaultValue where owner/managers
  // might see the wrong default tab selected before user roles are known
  if (needsLoadingGate) {
    return null;
  }

  // Fallback when no tabs are visible (shouldn't happen if parent checks visibility correctly)
  if (!hasTabs) {
    return <NoTabs />;
  }

  return (
    <Tabs
      value={currentTab}
      onValueChange={handleTabChange}
      className="h-[calc(100vh-11rem)] flex flex-col gap-6"
    >
      <TabsList
        // Hidden under the UI v2 shell, which lists the sub-tabs in its sidebar.
        data-inventory-tabbar
        className={`grid w-full grid-cols-1 h-auto sm:h-9 ${tabsListGridCols}`}
      >
        {triggers}
      </TabsList>
      {contents}
    </Tabs>
  );
};
